/**
 * ClientEquipmentTab — extracted from Clients.jsx in iter170
 * component split step #3. Owns the customer-detail "Equipment" tab:
 *   • Search bar + Add Equipment dialog trigger
 *   • Add/Edit Equipment dialog (form with scope picker, library
 *     import, material/labor/other-budget sections, totals, service
 *     type, dates, notes, service history)
 *   • Equipment list table with inline note editing, edit/archive,
 *     and "Create WO" shortcut.
 *
 * Stateless presentation component — every piece of state lives on
 * the Clients.jsx parent so the dialog stays in sync with the
 * scope/library/service-type lookups and the WO tab ref. The prop
 * surface is large (~30 props) but mechanical; passing one big
 * `ctx` bag would obscure what this file actually touches.
 */
import { format } from "date-fns";
import { shortDate } from "@/lib/format";
import {
  Plus, X, Wrench, Clock, ClipboardList, Trash2, ArrowUpDown,
} from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { NoteLog } from "@/components/NoteLog";

import { formatCurrency } from "@/pages/clients/clientsUtils";
import { BTN, btn } from "@/components/common/btn";
import SearchBar from "@/components/common/SearchBar";

export function ClientEquipmentTab({
  // Search / dialog open state
  equipmentSearch, setEquipmentSearch,
  isEquipmentDialogOpen, setIsEquipmentDialogOpen,
  // Form state
  equipmentFormData, setEquipmentFormData,
  editingEquipment,
  importedFromLibrary,
  saveToLibrary, setSaveToLibrary,
  isAddingServiceType, setIsAddingServiceType,
  newServiceType, setNewServiceType,
  equipmentSiteFilter, setEquipmentSiteFilter,
  inlineNoteEquipId, setInlineNoteEquipId,
  inlineNoteText, setInlineNoteText,
  // Lookup lists
  masterScopes, libraryOptions, clientSites, serviceTypes,
  filteredEquipment, uniqueEquipmentSites,
  selectedSiteId,
  // Handlers
  handleEquipmentSubmit, resetEquipmentForm, handleLibrarySelect,
  handleAddServiceType, handleEditEquipment, handleArchiveEquipment,
  handleInlineNoteAdd, toggleEquipmentSort,
  getSiteNameById,
  setActiveDetailTab,
  workOrderTabRef,
}) {
  return (
    <TabsContent value="equipment" className="mt-4">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <SearchBar value={equipmentSearch} onChange={setEquipmentSearch} placeholder="Search equipment..." testId="search-equipment" />
        <Dialog open={isEquipmentDialogOpen} onOpenChange={setIsEquipmentDialogOpen}>
          <DialogTrigger asChild>
            <button type="button" className={`${btn("write")} w-7 justify-center !px-0`} onClick={resetEquipmentForm} data-testid="btn-add-equipment" title="Add equipment" aria-label="Add equipment">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEquipment ? "Edit equipment" : "Add equipment"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEquipmentSubmit} className="space-y-4">
              {/* Step 1: Select Scope */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <Label className="text-xs font-semibold text-brand">1. Select scope</Label>
                {importedFromLibrary ? (
                  <div className="h-9 flex items-center px-3 bg-white rounded-md text-sm text-slate-600 border">{equipmentFormData.scope_name || "—"}</div>
                ) : (
                  <SearchableSelect
                    value={equipmentFormData.scope_name || "none"}
                    onValueChange={(value) => setEquipmentFormData({ ...equipmentFormData, scope_name: value === "none" ? "" : value, manufacturer_name: "" })}
                    options={[...masterScopes]
                      .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                      .map(s => ({ value: s.name, label: s.name }))}
                    placeholder="Select scope..."
                    searchPlaceholder="Search scopes..."
                    className="w-full"
                    data-testid="select-scope"
                  />
                )}
              </div>
              {/* Step 2: Import from Equipment Library (filtered by scope) */}
              {!editingEquipment && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <Label className="text-xs font-semibold text-slate-500">2. Import from library {equipmentFormData.scope_name ? <span className="text-brand">({equipmentFormData.scope_name})</span> : <span className="text-slate-400">(select scope first to filter)</span>}</Label>
                  <SearchableSelect
                    value="none"
                    onValueChange={handleLibrarySelect}
                    options={libraryOptions}
                    placeholder={equipmentFormData.scope_name ? `Search ${equipmentFormData.scope_name} equipment & parts...` : "Search all equipment & parts..."}
                    searchPlaceholder="Type to search equipment, parts, part numbers..."
                    className="w-full"
                    popoverWidth="560px"
                    data-testid="library-search"
                  />
                </div>
              )}
              {/* Site, Location, Cost Code, Department */}
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Site</Label>
                  <SearchableSelect
                    value={equipmentFormData.site_id || "none"}
                    onValueChange={(value) => setEquipmentFormData({ ...equipmentFormData, site_id: value === "none" ? "" : value })}
                    options={clientSites.map(s => ({ value: s.id, label: s.name }))}
                    placeholder="Select site"
                    searchPlaceholder="Search sites..."
                    className="w-full"
                    data-testid="select-equipment-site"
                  />
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Input value={equipmentFormData.location || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, location: e.target.value })} placeholder="e.g. Gym, Lobby, Roof" data-testid="input-equipment-location" />
                </div>
                <div>
                  <Label className="text-xs">Cost Code</Label>
                  <Input value={equipmentFormData.cost_code || "20-100000"} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, cost_code: e.target.value })} className="font-mono" data-testid="input-equipment-costcode" />
                </div>
                <div>
                  <Label className="text-xs">Department</Label>
                  <Input value={equipmentFormData.department || "Service"} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, department: e.target.value })} data-testid="input-equipment-department" />
                </div>
              </div>

              {/* Equipment Name & Part Number & Quantity */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={equipmentFormData.name || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, name: e.target.value })} placeholder="e.g. RTU-1, Main Boiler" data-testid="input-equipment-name" />
                </div>
                <div>
                  <Label className="text-xs">Part Number</Label>
                  <Input value={equipmentFormData.part_number || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, part_number: e.target.value })} placeholder="Optional" className="placeholder:text-slate-400" data-testid="input-part-number" />
                </div>
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input
                    type="number" min="1" step="1"
                    value={equipmentFormData.qty ?? ""}
                    onChange={(e) => setEquipmentFormData({ ...equipmentFormData, qty: e.target.value })}
                    onBlur={(e) => {
                      const n = parseInt(e.target.value);
                      setEquipmentFormData(prev => ({ ...prev, qty: !n || n < 1 ? 1 : n }));
                    }}
                    className="h-9 text-sm"
                    data-testid="input-equipment-qty"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Part Description</Label>
                <Input value={equipmentFormData.description || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, description: e.target.value })} placeholder="Optional" className="placeholder:text-slate-400" data-testid="input-part-description" />
              </div>

              {/* === MATERIAL section ============================
                  Pure UI grouping. Inputs / handlers / data-testids
                  are byte-identical to the legacy flat layout —
                  the redesign only adds wrapper cards, section
                  headers, and a right-aligned subtotal pill. */}
              <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">Material</span>
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-slate-200 rounded-md">
                    <span className="text-[11px] text-slate-500">Total material</span>
                    <span className="text-sm font-bold tabular-nums text-slate-900">
                      {(() => { const c = parseFloat(equipmentFormData.part_cost) || 0; const gm = parseFloat(equipmentFormData.material_margin) || 0; return formatCurrency(gm >= 100 ? c : c / (1 - gm / 100)); })()}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Material Cost</Label>
                    <Input type="number" step="0.01" value={equipmentFormData.part_cost || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, part_cost: e.target.value })} className="h-9 text-sm bg-white" data-testid="input-part-cost" />
                  </div>
                  <div>
                    <Label className="text-xs">Mat GM%</Label>
                    <Input type="number" step="1" value={Math.round(parseFloat(equipmentFormData.material_margin) || 0)} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, material_margin: e.target.value })} className="h-9 text-sm bg-white" data-testid="input-material-margin" />
                  </div>
                  <div>
                    <Label className="text-xs">Freight</Label>
                    <Input type="number" step="0.01" value={equipmentFormData.freight || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, freight: e.target.value })} className="h-9 text-sm bg-white" data-testid="input-freight" />
                  </div>
                </div>
              </div>

              {/* === LABOR section ================================ */}
              <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-slate-500">Labor</span>
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-slate-200 rounded-md">
                    <span className="text-[11px] text-slate-500">Total labor</span>
                    <span className="text-sm font-bold tabular-nums text-slate-900">
                      {(() => { const c = parseFloat(equipmentFormData.labor_cost) || 0; const gm = parseFloat(equipmentFormData.labor_margin) || 0; const rate = gm >= 100 ? c : Math.ceil(c / (1 - gm / 100)); return formatCurrency((parseFloat(equipmentFormData.labor_hours) || 0) * rate); })()}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Labor cost ($/hr)</Label>
                    <Input type="number" step="0.5" value={equipmentFormData.labor_cost ?? ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, labor_cost: e.target.value })} className="h-9 text-sm bg-white" data-testid="input-labor-cost" />
                  </div>
                  <div>
                    <Label className="text-xs">Lab GM%</Label>
                    <Input type="number" step="1" value={Math.round(parseFloat(equipmentFormData.labor_margin) || 0)} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, labor_margin: e.target.value })} className="h-9 text-sm bg-white" data-testid="input-labor-margin" />
                  </div>
                  <div>
                    <Label className="text-xs">$/Hr (Rate)</Label>
                    <div className="h-9 flex items-center px-2 bg-slate-100 border border-slate-200 rounded-md text-sm font-medium tabular-nums">
                      {(() => { const c = parseFloat(equipmentFormData.labor_cost) || 0; const gm = parseFloat(equipmentFormData.labor_margin) || 0; return formatCurrency(gm >= 100 ? c : Math.ceil(c / (1 - gm / 100))); })()}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Hours</Label>
                    <Input type="number" step="0.5" value={equipmentFormData.labor_hours || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, labor_hours: e.target.value })} className="h-9 text-sm bg-white" data-testid="input-labor-hours" />
                  </div>
                </div>
              </div>

              {/* === OTHER section (Equipment / Disposal / Travel) ==
                   LUMP-SUM BUDGETS with GM% markup. Each card has
                   a Cost input (left) and a GM% input (right).
                   Sell price = cost / (1 - gm/100), same formula
                   as Material. Defaults to 25% GM on new equipment.
                   These are NOT multiplied by the top-level
                   Equipment Quantity (a $500 disposal sells once
                   whether the equipment qty is 1 or 5). */}
              <div className="border border-slate-200 rounded-lg bg-slate-50/60 p-3 space-y-2">
                {(() => {
                  const sell = (key) => {
                    const c = parseFloat(equipmentFormData[`${key}_cost`]) || 0;
                    const gm = parseFloat(equipmentFormData[`${key}_margin`]) || 0;
                    return c > 0 ? (gm >= 100 ? c : c / (1 - gm / 100)) : 0;
                  };
                  const totalOther = sell("equipment") + sell("disposal") + sell("travel");
                  return (
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-slate-500">Other</span>
                      <div className="flex items-center gap-2 px-2.5 py-1 bg-white border border-slate-200 rounded-md">
                        <span className="text-[11px] text-slate-500">Total other</span>
                        <span className="text-sm font-bold tabular-nums text-slate-900">{formatCurrency(totalOther)}</span>
                      </div>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "equipment", label: "Equipment" },
                    { key: "disposal", label: "Disposal" },
                    { key: "travel", label: "Travel" },
                  ].map(({ key, label }) => {
                    const c = parseFloat(equipmentFormData[`${key}_cost`]) || 0;
                    const dormant = c === 0;
                    return (
                      <div key={key} className={`rounded-md border bg-white p-2 transition-opacity ${dormant ? "opacity-60 border-slate-200" : "border-slate-300"}`}>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-[11px] font-medium text-slate-600">{label}</Label>
                          <Label className="text-[11px] font-medium text-slate-500">GM%</Label>
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_56px] gap-1">
                          <Input type="number" step="0.01" value={equipmentFormData[`${key}_cost`] || ""} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, [`${key}_cost`]: e.target.value })} placeholder="Cost" className="h-9 text-sm" data-testid={`input-${key}-cost`} />
                          <Input type="number" step="1" value={Math.round(parseFloat(equipmentFormData[`${key}_margin`]) || 0)} onChange={(e) => setEquipmentFormData({ ...equipmentFormData, [`${key}_margin`]: e.target.value })} placeholder="%" className="h-9 text-sm text-center px-1" data-testid={`input-${key}-margin`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Totals summary (matching Edit Part) */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
                {(() => {
                  const qty = parseInt(equipmentFormData.qty) || 1;
                  const matC = parseFloat(equipmentFormData.part_cost) || 0;
                  const matGM = parseFloat(equipmentFormData.material_margin) || 0;
                  const totalMat = matGM >= 100 ? matC : matC / (1 - matGM / 100);
                  const labC = parseFloat(equipmentFormData.labor_cost) || 0;
                  const labGM = parseFloat(equipmentFormData.labor_margin) || 0;
                  const rate = labGM >= 100 ? labC : Math.ceil(labC / (1 - labGM / 100));
                  const totalLab = (parseFloat(equipmentFormData.labor_hours) || 0) * rate;
                  const freight = parseFloat(equipmentFormData.freight) || 0;
                  // Other buckets are LUMP-SUM budgets with GM%
                  // markup. Pass-through with margin: sell price
                  // = cost / (1 - gm/100), same formula as Material.
                  // They do NOT scale with the top-level Equipment
                  // Quantity. They land on the WO at qty=1,
                  // unit_price = sell price.
                  const eC = parseFloat(equipmentFormData.equipment_cost) || 0;
                  const eGM = parseFloat(equipmentFormData.equipment_margin) || 0;
                  const eSell = eC > 0 ? (eGM >= 100 ? eC : eC / (1 - eGM / 100)) : 0;
                  const dC = parseFloat(equipmentFormData.disposal_cost) || 0;
                  const dGM = parseFloat(equipmentFormData.disposal_margin) || 0;
                  const dSell = dC > 0 ? (dGM >= 100 ? dC : dC / (1 - dGM / 100)) : 0;
                  const tC = parseFloat(equipmentFormData.travel_cost) || 0;
                  const tGM = parseFloat(equipmentFormData.travel_margin) || 0;
                  const tSell = tC > 0 ? (tGM >= 100 ? tC : tC / (1 - tGM / 100)) : 0;
                  const otherTotal = eSell + dSell + tSell;
                  const otherCost = eC + dC + tC;
                  const perUnit = totalMat + freight + totalLab;
                  const grand = perUnit * qty + otherTotal;
                  const totalCost = (matC + freight + (parseFloat(equipmentFormData.labor_hours) || 0) * labC) * qty + otherCost;
                  const gm = grand > 0 ? ((grand - totalCost) / grand * 100) : 0;
                  return (
                    <>
                      <div className="flex justify-between text-slate-500">
                        <span>Per unit</span>
                        <span className="tabular-nums">{formatCurrency(perUnit)}</span>
                      </div>
                      {qty > 1 && (
                        <div className="flex justify-between text-slate-500">
                          <span>Quantity</span>
                          <span className="tabular-nums">&times; {qty}</span>
                        </div>
                      )}
                      {otherTotal > 0 && (
                        <div className="flex justify-between text-slate-500">
                          <span>Other (lump sum)</span>
                          <span className="tabular-nums">+ {formatCurrency(otherTotal)}</span>
                        </div>
                      )}
                      <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between items-center">
                        <span className="font-medium text-slate-700">Total</span>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs tabular-nums">GM: {gm.toFixed(1)}%</span>
                          <span className="font-bold text-base tabular-nums">{formatCurrency(grand)}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Service Type */}
              <div>
                <Label className="text-xs">Service Type</Label>
                {isAddingServiceType ? (
                  <div className="flex gap-2">
                    <Input value={newServiceType} onChange={(e) => setNewServiceType(e.target.value)} placeholder="Enter service type" />
                    <Button type="button" size="sm" onClick={handleAddServiceType}>Add</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setIsAddingServiceType(false)}><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <SearchableSelect
                    value={equipmentFormData.service_type || "none"}
                    onValueChange={(value) => setEquipmentFormData({ ...equipmentFormData, service_type: value === "none" ? "" : value })}
                    options={serviceTypes.map(st => ({ value: st.name, label: st.name }))}
                    placeholder="Select service type"
                    searchPlaceholder="Search service types..."
                    onAddNew={() => setIsAddingServiceType(true)}
                    addLabel="Add new service type"
                    className="w-full"
                    data-testid="select-service-type"
                  />
                )}
              </div>

              {/* Dates side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Service Date</Label>
                  <Input
                    type="date"
                    value={equipmentFormData.install_date ? (equipmentFormData.install_date instanceof Date ? equipmentFormData.install_date.toISOString().split('T')[0] : equipmentFormData.install_date.split('T')[0]) : ""}
                    onChange={(e) => setEquipmentFormData({ ...equipmentFormData, install_date: e.target.value ? new Date(e.target.value + 'T00:00:00') : null })}
                    data-testid="input-install-date"
                  />
                </div>
                <div>
                  <Label>Last Serviced</Label>
                  <Input
                    type="date"
                    value={equipmentFormData.last_serviced ? (equipmentFormData.last_serviced instanceof Date ? equipmentFormData.last_serviced.toISOString().split('T')[0] : equipmentFormData.last_serviced.split('T')[0]) : ""}
                    onChange={(e) => setEquipmentFormData({ ...equipmentFormData, last_serviced: e.target.value ? new Date(e.target.value + 'T00:00:00') : null })}
                    data-testid="input-last-serviced"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <NoteLog
                  notes={Array.isArray(equipmentFormData.notes) ? equipmentFormData.notes : []}
                  onChange={(updated) => setEquipmentFormData({ ...equipmentFormData, notes: updated })}
                />
              </div>

              {/* Service History (read-only) */}
              {editingEquipment && editingEquipment.service_history && editingEquipment.service_history.length > 0 && (
                <div>
                  <Label>Service History</Label>
                  <div className="bg-slate-50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {editingEquipment.service_history.map((entry, idx) => (
                      // eslint-disable-next-line react/no-array-index-key -- service_history is append-only and ordered; no stable id on entries
                      <div key={`sh-${idx}-${entry.date || ''}`} className="text-sm py-1.5 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="font-medium num">{shortDate(entry.date)}</span>
                        </div>
                        {entry.notes && (
                          <div className="text-xs text-slate-500 mt-1 ml-5">{entry.notes}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!editingEquipment && (
                <div className={`flex items-center gap-2 pt-1 ${importedFromLibrary ? "opacity-50" : ""}`}>
                  <input type="checkbox" id="saveToLibrary" checked={saveToLibrary} onChange={(e) => setSaveToLibrary(e.target.checked)} disabled={importedFromLibrary} className="rounded border-slate-300" />
                  <label htmlFor="saveToLibrary" className="text-xs text-slate-500">Save to equipment library</label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsEquipmentDialogOpen(false)}>
                  Cancel
                </Button>
                {!editingEquipment && (
                  <Button type="button" variant="secondary" data-testid="btn-save-add-another" onClick={(e) => handleEquipmentSubmit(e, true)}>
                    Save & add another
                  </Button>
                )}
                <Button type="submit" className={BTN.write} data-testid="btn-save-equipment">
                  {editingEquipment ? "Update" : "Add"} equipment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <span className="ml-auto text-xs text-slate-500 num">{filteredEquipment.length} unit{filteredEquipment.length === 1 ? "" : "s"}</span>
      </div>

      {/* Equipment List - Table View */}
      {filteredEquipment.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p>No equipment {selectedSiteId !== "all" ? "for this site" : "added yet"}</p>
        </div>
      ) : (
        <div className="data-table-container overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="th-std p-1">Equipment</TableHead>
                <TableHead className="th-std p-1 w-12 text-center">Qty</TableHead>
                <TableHead className="th-std p-1">Part #</TableHead>
                <TableHead className="th-std p-1">Location</TableHead>
                <TableHead className="th-std p-1">
                  <Select value={equipmentSiteFilter || "all"} onValueChange={(v) => setEquipmentSiteFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-7 border-0 shadow-none text-xs font-semibold px-1" data-testid="filter-equipment-site">
                      <SelectValue placeholder="Site" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Site</SelectItem>
                      {uniqueEquipmentSites.map((site) => (
                        <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableHead>
                <TableHead className="th-std p-1">
                  <Button variant="ghost" size="sm" className="h-7 px-1 text-xs font-semibold" onClick={() => toggleEquipmentSort("last_serviced")} data-testid="sort-equipment-serviced">
                    Last Serviced <ArrowUpDown className="ml-1 h-3 w-3 inline" />
                  </Button>
                </TableHead>
                <TableHead className="th-std">Notes</TableHead>
                <TableHead className="th-std w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEquipment.map(equip => (
                <TableRow key={equip.id} className="table-row-hover" data-testid={`equipment-row-${equip.id}`}>
                  <TableCell className="p-1 text-xs font-medium cursor-pointer hover:text-brand" onClick={() => handleEditEquipment(equip)}>
                    {equip.name || <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="p-1 text-xs text-slate-700 text-center" data-testid={`equip-qty-${equip.id}`}>
                    {equip.qty || 1}
                  </TableCell>
                  <TableCell className="p-1 text-xs text-slate-600" data-testid={`equip-partnum-${equip.id}`}>
                    {equip.part_number || <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="p-1 text-xs text-slate-600" data-testid={`equip-location-${equip.id}`}>
                    {equip.location || <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="p-1 text-xs text-slate-600 cursor-pointer" onClick={() => handleEditEquipment(equip)}>
                    {equip.site_id ? getSiteNameById(equip.site_id) : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-xs cursor-pointer" onClick={() => handleEditEquipment(equip)} title="Click to edit dates">
                    {(equip.last_serviced || equip.install_date) ? (
                      <span className="text-emerald-600 num">{shortDate(equip.last_serviced || equip.install_date)}</span>
                    ) : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-xs p-1 max-w-[200px]" onClick={(e) => e.stopPropagation()}>
                    {inlineNoteEquipId === equip.id ? (
                      <div className="flex gap-1 items-center">
                        <Input
                          value={inlineNoteText}
                          onChange={(e) => setInlineNoteText(e.target.value)}
                          placeholder="Add note..."
                          className="h-6 text-xs"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleInlineNoteAdd(equip.id, inlineNoteText);
                              setInlineNoteEquipId(null);
                              setInlineNoteText("");
                            }
                            if (e.key === "Escape") {
                              setInlineNoteEquipId(null);
                              setInlineNoteText("");
                            }
                          }}
                          data-testid={`inline-note-input-${equip.id}`}
                        />
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-1 cursor-pointer group"
                        onClick={() => { setInlineNoteEquipId(equip.id); setInlineNoteText(""); }}
                        title={Array.isArray(equip.notes) && equip.notes.length > 0
                          ? equip.notes.map(n => `${n.date || ""}: ${n.text}`).join("\n")
                          : "Click to add note"}
                      >
                        {Array.isArray(equip.notes) && equip.notes.length > 0 ? (
                          <div className="truncate">
                            <span className="text-slate-500 text-xs">{equip.notes[0].text}</span>
                            <span className="text-slate-400 text-[10px] ml-1">
                              {equip.notes[0].date ? format(new Date(equip.notes[0].date + "T00:00:00"), 'M/d/yy') : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                        <Plus className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="p-1 text-xs">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                        setActiveDetailTab("workorders");
                        setTimeout(() => workOrderTabRef.current?.openNew([equip.id]), 100);
                      }} data-testid={`create-wo-${equip.id}`} title="Create Work Order">
                        <ClipboardList className="w-3.5 h-3.5 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => handleArchiveEquipment(e, equip.id)}
                        data-testid={`archive-equipment-${equip.id}`}
                        title="Move to history"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </TabsContent>
  );
}
